/**
 * Native checkbox/radio helpers — replaces iCheck.
 * Bootstrap 5 `.form-check` markup + native `input.checked`/`input.indeterminate` are the
 * source of truth; no wrapper elements are created or DOM structure changed here.
 */

type OptionalInput = HTMLInputElement | null | undefined;

function isCheckableInput(input: OptionalInput): input is HTMLInputElement {
    return input instanceof HTMLInputElement && (input.type === 'checkbox' || input.type === 'radio');
}

function isCheckboxInput(input: OptionalInput): input is HTMLInputElement {
    return input instanceof HTMLInputElement && input.type === 'checkbox';
}

function isRadioInput(input: OptionalInput): input is HTMLInputElement {
    return input instanceof HTMLInputElement && input.type === 'radio';
}

export function setChecked(input: OptionalInput, checked: boolean, emit = true): void {
    if (!isCheckboxInput(input)) {
        return;
    }

    input.indeterminate = false;
    input.checked = checked;
    if (emit) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

export function setRadioChecked(input: OptionalInput, emit = true): void {
    if (!isRadioInput(input)) {
        return;
    }

    input.checked = true;
    if (emit) {
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }
}

export function setIndeterminate(input: OptionalInput, indeterminate: boolean): void {
    if (!isCheckboxInput(input)) {
        return;
    }

    input.indeterminate = indeterminate;
    if (indeterminate) {
        input.checked = false;
    }
    const wrap = input.closest<HTMLElement>('.nais-role-privileges__check, .form-check');
    wrap?.classList.toggle('is-indeterminate', indeterminate);
}

/**
 * Doplní Bootstrap 5 classes/aria tam, kde je legacy markup přímo nedodává.
 * Nevytváří wrappery, neupravuje strom DOM.
 */
export function initNativeChecks(root: ParentNode | Document = document): void {
    const scope: ParentNode = root;
    const inputs = new Set<HTMLInputElement>();

    if (root instanceof HTMLInputElement && isCheckableInput(root)) {
        inputs.add(root);
    }

    scope.querySelectorAll<HTMLInputElement>("input[type='checkbox'], input[type='radio']").forEach((input) => {
        inputs.add(input);
    });

    inputs.forEach((input) => {
        if (!input.classList.contains('form-check-input')) {
            input.classList.add('form-check-input');
        }

        if (
            !input.hasAttribute('aria-label')
            && !input.hasAttribute('aria-labelledby')
            && !input.id
        ) {
            return;
        }

        if (input.id) {
            const hasLabel = scope.querySelector(`label[for="${CSS.escape(input.id)}"]`)
                || input.closest('label');
            if (!hasLabel && !input.hasAttribute('aria-label') && !input.hasAttribute('aria-labelledby')) {
                const title = input.getAttribute('title');
                if (title) {
                    input.setAttribute('aria-label', title);
                }
            }
        }
    });
}

declare global {
    interface Window {
        initNativeChecks?: typeof initNativeChecks;
        setNativeChecked?: typeof setChecked;
        setNativeRadioChecked?: typeof setRadioChecked;
        setNativeIndeterminate?: typeof setIndeterminate;
    }
}

window.initNativeChecks = initNativeChecks;
window.setNativeChecked = setChecked;
window.setNativeRadioChecked = setRadioChecked;
window.setNativeIndeterminate = setIndeterminate;
